import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { ASSETS, readViteEnv } from '@swim/config'
import { RouteLoadingFallback } from './RouteLoadingFallback'
import { FooterVisibilityContext } from './chromeless-footer-context'

// Landing app per environment (docker-entrypoint.sh sets it from SWIM_ENV). Production fallback if unset.
const LANDING_URL = readViteEnv(import.meta.env, 'VITE_LANDING_URL', 'https://swimflows.com')

/**
 * Minimal shell for merchant subdomain storefronts ({slug}.swimflows.com).
 * No Swim chrome — no nav, no logo, no sidebar.
 * Only the stream content and a tasteful "Powered by SWIM" footer.
 * A child may hide the footer via useSetChromelessFooterHidden (stream-unavailable 404).
 *
 * Preserves the same scroll container id ("main-content") as Layout so
 * StreamPage's IntersectionObserver works identically in both contexts.
 */
export function ChromelessLayout() {
  const { t } = useTranslation('common')
  const [footerHidden, setFooterHidden] = useState(false)
  return (
    <div className="h-screen overflow-hidden bg-sand-200 dark:bg-slate-900 bg-dot-pattern flex flex-col">
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 overflow-y-auto outline-none"
      >
        <div className="min-h-full flex flex-col">
          <div className="flex-1">
            <FooterVisibilityContext.Provider value={setFooterHidden}>
              <Suspense fallback={<RouteLoadingFallback />}>
                <Outlet />
              </Suspense>
            </FooterVisibilityContext.Provider>
          </div>
          {!footerHidden && (
            <footer className="py-5 text-center border-t border-slate-200 dark:border-slate-800/60">
              <span className="block text-[11px] text-slate-400 dark:text-slate-500">
                {t('streamUnavailable.poweredBy', { ns: 'errors' })}
              </span>
              <a
                href={LANDING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-500"
              >
                <img
                  src={ASSETS.logos.horizontal.black.sm}
                  alt={t('streamUnavailable.logoLabel', { ns: 'errors' })}
                  className="h-5 w-auto opacity-70 hover:opacity-100 transition-opacity dark:hidden"
                />
                <img
                  src={ASSETS.logos.horizontal.white.sm}
                  alt=""
                  aria-hidden
                  className="hidden h-5 w-auto opacity-70 hover:opacity-100 transition-opacity dark:block"
                />
              </a>
            </footer>
          )}
        </div>
      </main>
    </div>
  )
}
